import { Flex } from '@mantine/core';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import CashflowContent from 'src/cashflows/CashflowContent';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import FileUploadPage, {
  FileUploadTabNames,
} from 'src/data-capture/file-management/FileUploadPage';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { FinancialContent } from 'src/financial/financial-data/FinancialContent';
import DeprecatedNonFinancialDataContent from 'src/non-financial-data/DeprecatedNonFanancialDataContent';
import NonFinancialDataContent from 'src/non-financial-data/NonFanancialDataContent';
import UnderlineTabs from 'src/UI/page-nav/UnderlineTabs';
import ClientMeetingsContent from 'src/meetings/client/ClientMeetingsContent';

export const ClientTabs = {
  NonFinancialData: 'non-financial-data',
  FinancialData: 'financial-data',
  Cashflows: 'cashflows',
  Documents: 'document',
  Meetings: 'meetings',
} as const;
type TabName = (typeof ClientTabs)[keyof typeof ClientTabs];

export default function ClientData() {
  const { clientId } = useCurrentClient();
  const { tab } = useParams<{ tab: TabName }>();
  const { flagEnabledRemoveDynamicUi, flagEnabledGlobalCalendarAccess } = useFlags();

  const tabContent = useMemo(() => {
    if (tab === ClientTabs.NonFinancialData) {
      return flagEnabledRemoveDynamicUi ? (
        <NonFinancialDataContent />
      ) : (
        <DeprecatedNonFinancialDataContent />
      );
    }
    if (tab === ClientTabs.FinancialData) {
      return <FinancialContent />;
    }
    if (tab === ClientTabs.Cashflows) {
      return <CashflowContent />;
    }
    if (tab === ClientTabs.Documents) {
      return <FileUploadPage />;
    }
    if (tab === ClientTabs.Meetings && flagEnabledGlobalCalendarAccess) {
      return <ClientMeetingsContent />;
    }
    return null;
  }, [tab]);

  return (
    <Flex direction="column" gap="md" pos={'relative'}>
      <Flex direction="row" justify={'space-between'}>
        <UnderlineTabs
          mode="router"
          showClientSearch
          tabs={[
            {
              label: 'Profile',
              href: `/client/${clientId}/${ClientTabs.NonFinancialData}`,
            },
            {
              label: 'Financial',
              href: `/client/${clientId}/${ClientTabs.FinancialData}`,
            },
            {
              label: 'Cashflows',
              href: `/client/${clientId}/${ClientTabs.Cashflows}`,
            },
            {
              label: 'Documents',
              href: `/client/${clientId}/${ClientTabs.Documents}/${FileUploadTabNames.ToReview}`,
              matchHref: `/client/${clientId}/${ClientTabs.Documents}`,
            },
            {
              label: 'Meetings',
              href: `/client/${clientId}/${ClientTabs.Meetings}`,
              disabled: !flagEnabledGlobalCalendarAccess,
            },
          ].filter(t => !t.disabled)}
        />
      </Flex>

      {tabContent}
    </Flex>
  );
}
